// ============================================
// Vendor Password Gate
// Protects vendor portal with server-side authentication
// ============================================

import { useState, type ReactNode } from 'react';
import { Lock, AlertCircle, Loader2 } from 'lucide-react';
import { vendorAuthenticate } from '../../services/firestore/orderService';

const TOKEN_KEY = 'vendor_token';

interface VendorGateProps {
  children: ReactNode;
}

export function VendorGate({ children }: VendorGateProps) {
  const [authenticated, setAuthenticated] = useState(
    () => !!sessionStorage.getItem(TOKEN_KEY)
  );
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = await vendorAuthenticate(password);
      sessionStorage.setItem(TOKEN_KEY, token);
      setAuthenticated(true);
    } catch (err: unknown) {
      // If 401 or any auth failure, clear any stale token
      sessionStorage.removeItem(TOKEN_KEY);

      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  if (authenticated) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-bg-deep flex items-center justify-center p-4">
      <div
        className="anim-fade-in-up w-full max-w-md bg-bg-card border border-bg-tertiary rounded-2xl p-8 shadow-xl"
      >
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-full bg-accent-light/10 flex items-center justify-center mb-4">
            <Lock size={28} className="text-accent-light" />
          </div>
          <h1 className="text-2xl font-display text-text-primary mb-1">
            <span className="text-gold">Hemologix</span> Vendor Portal
          </h1>
          <p className="text-text-muted text-base">
            Enter your vendor password to continue
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="vendor-password"
              className="block text-sm font-medium text-text-secondary mb-2"
            >
              Password
            </label>
            <input
              id="vendor-password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError('');
              }}
              placeholder="Enter vendor password"
              className="w-full px-4 py-3 rounded-xl bg-bg-elevated border border-bg-tertiary text-text-primary text-base placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-light/50 focus:border-accent-light transition-colors"
              autoFocus
              disabled={loading}
            />
          </div>

          {error && (
            <div
              className="anim-fade-in-down flex items-center gap-2 text-critical text-sm"
            >
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-accent-light text-white font-medium text-base hover:bg-accent-light/90 transition-colors focus:outline-none focus:ring-2 focus:ring-accent-light/50 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Authenticating...
              </>
            ) : (
              'Access Portal'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
